/**
 * Agos — Filipino / English language strings
 * Usage: const { t, lang, setLang } = useLanguage();
 */

export type Lang = 'en' | 'fil';

export const translations = {
  en: {
    // Nav
    nav_verify: 'Verify',
    nav_pricing: 'Pricing',
    nav_dashboard: 'Dashboard',
    nav_login: 'Log in',
    nav_register: 'Get Started',
    nav_logout: 'Log out',

    // Landing
    hero_title: 'Philippine eKYC — Instant & Secure',
    hero_sub: 'Verify any government-issued ID in seconds. BSP-aligned, NPC-compliant.',
    hero_cta: 'Start Verification',
    hero_cta2: 'See Pricing',

    // Verification wizard steps
    step_bundle: 'Bundle',
    step_type: 'Type',
    step_id: 'ID Type',
    step_upload: 'Upload',
    step_review: 'Review',
    step_consent: 'Consent',
    step_liveness: 'Liveness',
    step_processing: 'Processing',
    step_result: 'Result',

    // Bundle step
    bundle_title: 'Choose Your Bundle',
    bundle_sub: 'Select a bundle to get started. Each bundle includes credits for multiple verifications.',
    bundle_basic_credits: 'Basic Credits',
    bundle_enhanced_credits: 'Enhanced Credits',
    bundle_info: 'Each verification uses 1 credit. Unused credits never expire.',
    bundle_continue: 'Continue',

    // Type step
    type_title: 'Choose Verification Type',
    type_sub: 'Select the level of verification you need',
    type_basic: 'Basic',
    type_basic_desc: 'Face Match + Liveness + Trusted Results',
    type_enhanced: 'Enhanced',
    type_enhanced_desc: 'Basic + AI OCR Extraction & Validation',
    type_recommended: 'Recommended',

    // ID select
    id_title: 'Select ID Type',
    id_sub: 'Choose the government-issued ID you will provide',
    id_primary: 'Primary',

    // Upload step
    upload_title: 'Upload ID Document',
    upload_front: 'Front of ID',
    upload_back: 'Back of ID',
    upload_back_optional: 'Optional but recommended',
    upload_take_photo: 'Take Photo',
    upload_upload_file: 'Upload File',
    upload_extract: 'Extract & Verify',
    upload_extracting: 'Extracting...',
    upload_file_hint: 'JPG, PNG or PDF — Max 10MB',
    upload_quality_good: 'Good Quality',
    upload_quality_issues: 'Quality Issues Detected',
    upload_retake: 'Retake Photo',
    upload_use_anyway: 'Use Anyway',

    // OCR Review
    ocr_title: 'Review Extracted Data',
    ocr_sub: 'Verify that the information below matches your ID',
    ocr_authentic: 'Document appears authentic',
    ocr_will_verify: 'Authenticity will be verified by server',
    ocr_qr_verified: 'PhilSys QR format validated — data matches',
    ocr_qr_mismatch: 'Warning: QR data does not match document',
    ocr_continue: 'Continue',
    ocr_reupload: 'Re-upload',

    // Consent
    consent_title: 'Biometric Data Consent',
    consent_sub: 'Pahintulot sa Paggamit ng Biometric Data',
    consent_purpose: 'Purpose: To match your face against your submitted ID.',
    consent_retention: 'Retention: Stored securely per our Privacy Policy.',
    consent_security: 'Security: Encrypted at rest and during transmission.',
    consent_check: 'I understand and agree to the collection and processing of my biometric data.',
    consent_proceed: 'Proceed to Liveness',

    // Liveness
    liveness_title: 'Liveness Check',
    liveness_sub: 'Take a selfie to verify you are a real person and match your ID',
    liveness_use_camera: 'Use Camera',
    liveness_mobile_handoff: 'Mobile Handoff',
    liveness_start: 'Start Liveness Wizard',
    liveness_passed: 'Liveness: Verified',
    liveness_failed: 'Liveness: Failed',
    liveness_retake: 'Retake',
    liveness_complete: 'Complete Purchase',
    liveness_processing: 'Processing...',

    // Processing
    processing_title: 'Processing Verification',
    processing_sub: 'Server-side identity verification in progress...',
    processing_uploading: 'Uploading documents to server',
    processing_ocr: 'Server OCR + face matching',
    processing_scoring: 'Computing confidence score',
    processing_record: 'Generating secure verification record...',

    // Result
    result_title: 'Authenticity Assessment Complete',
    result_verified: 'Verified',
    result_download: 'Download Digital ID',
    result_new: 'New Verification',
    result_failed_title: 'Verification Failed',
    result_failed_sub: 'We could not verify your identity. Please try again.',
    result_try_again: 'Try Again',
    result_reasons: 'Reasons:',
    result_signed_note: 'Your Digital ID is cryptographically signed with RSA-4096. Banks and employers can verify its authenticity instantly using the Agos public verification portal or by scanning the QR code on the document.',

    // Footer
    footer_product: 'Product',
    footer_company: 'Company',
    footer_legal: 'Legal',
    footer_verify_record: 'Verify a Record',
    footer_pricing: 'Pricing',
    footer_dashboard: 'Dashboard',
    footer_faq: 'FAQ',
    footer_about: 'About Us',
    footer_overview: 'Product Overview',
    footer_security: 'Security Whitepaper',
    footer_contact: 'Contact',
    footer_privacy: 'Privacy Policy',
    footer_terms: 'Terms of Service',
    footer_brand_desc: 'The premium identity verification protocol for the Philippines. Engineering trust through cryptographic excellence.',
    footer_npc_badge: 'NPC COMPLIANT • DPA READY',
    footer_bsp_badge: 'BSP COMPLIANCE READY',
    footer_npc_notice: 'NPC registration in progress. Beta testing with synthetic data. Production deployment (Q2 2026) will use LightNode Manila with full NPC compliance and DPA-ready architecture.',
    footer_bsp_notice: 'Engineered for BSP eKYC Compliance',
  },

  fil: {
    // Nav
    nav_verify: 'I-Verify',
    nav_pricing: 'Presyo',
    nav_dashboard: 'Dashboard',
    nav_login: 'Mag-login',
    nav_register: 'Magsimula',
    nav_logout: 'Mag-logout',

    // Landing
    hero_title: 'Philippine eKYC — Mabilis at Ligtas',
    hero_sub: 'I-verify ang anumang government ID sa loob ng ilang segundo. Alinsunod sa BSP at NPC.',
    hero_cta: 'Magsimulang Mag-verify',
    hero_cta2: 'Tingnan ang Presyo',

    // Verification wizard steps
    step_bundle: 'Bundle',
    step_type: 'Uri',
    step_id: 'Uri ng ID',
    step_upload: 'I-upload',
    step_review: 'Suriin',
    step_consent: 'Pahintulot',
    step_liveness: 'Selfie',
    step_processing: 'Pinoproseso',
    step_result: 'Resulta',

    // Bundle step
    bundle_title: 'Pumili ng Bundle',
    bundle_sub: 'Pumili ng bundle para magsimula. Bawat bundle ay may kasamang credits para sa maraming verification.',
    bundle_basic_credits: 'Basic Credits',
    bundle_enhanced_credits: 'Enhanced Credits',
    bundle_info: 'Bawat verification ay gumagamit ng 1 credit. Ang mga hindi nagamit na credits ay hindi mag-e-expire.',
    bundle_continue: 'Magpatuloy',

    // Type step
    type_title: 'Pumili ng Uri ng Verification',
    type_sub: 'Piliin ang antas ng verification na kailangan mo',
    type_basic: 'Basic',
    type_basic_desc: 'Face Match + Liveness + Maaasahang Resulta',
    type_enhanced: 'Enhanced',
    type_enhanced_desc: 'Basic + AI OCR Extraction at Validation',
    type_recommended: 'Inirerekomenda',

    // ID select
    id_title: 'Pumili ng Uri ng ID',
    id_sub: 'Piliin ang government-issued ID na iyong ibibigay',
    id_primary: 'Pangunahin',

    // Upload step
    upload_title: 'I-upload ang ID Document',
    upload_front: 'Harap ng ID',
    upload_back: 'Likod ng ID',
    upload_back_optional: 'Opsyonal ngunit inirerekomenda',
    upload_take_photo: 'Kumuha ng Larawan',
    upload_upload_file: 'Mag-upload ng File',
    upload_extract: 'I-extract at I-verify',
    upload_extracting: 'Nag-e-extract...',
    upload_file_hint: 'JPG, PNG o PDF — Max 10MB',
    upload_quality_good: 'Magandang Kalidad',
    upload_quality_issues: 'May Nakitang Problema sa Kalidad',
    upload_retake: 'Kumuha Muli',
    upload_use_anyway: 'Gamitin Na Rin',

    // OCR Review
    ocr_title: 'Suriin ang Nakuha na Data',
    ocr_sub: 'Tiyaking ang impormasyon sa ibaba ay tumutugma sa iyong ID',
    ocr_authentic: 'Ang dokumento ay mukhang tunay',
    ocr_will_verify: 'Ibe-verify ng server ang pagiging tunay',
    ocr_qr_verified: 'Na-validate ang PhilSys QR format — tumutugma ang datos',
    ocr_qr_mismatch: 'Babala: Hindi tumutugma ang QR data sa dokumento',
    ocr_continue: 'Magpatuloy',
    ocr_reupload: 'Mag-upload Muli',

    // Consent
    consent_title: 'Pahintulot sa Biometric Data',
    consent_sub: 'Pahintulot sa Paggamit ng Biometric Data',
    consent_purpose: 'Layunin: Ihambing ang iyong mukha sa iyong isinumiteng ID.',
    consent_retention: 'Pananatili: Ligtas na naka-imbak alinsunod sa aming Patakaran sa Privacy.',
    consent_security: 'Seguridad: Naka-encrypt habang naka-imbak at habang ipinapadala.',
    consent_check: 'Naiintindihan ko at sumasang-ayon sa pagkolekta at pagproseso ng aking biometric data.',
    consent_proceed: 'Magpatuloy sa Liveness Check',

    // Liveness
    liveness_title: 'Liveness Check',
    liveness_sub: 'Kumuha ng selfie para ma-verify na ikaw ay tunay at tumutugma sa iyong ID',
    liveness_use_camera: 'Gumamit ng Camera',
    liveness_mobile_handoff: 'Gamitin ang Cellphone',
    liveness_start: 'Simulan ang Liveness Check',
    liveness_passed: 'Liveness: Na-verify',
    liveness_failed: 'Liveness: Nabigo',
    liveness_retake: 'Kumuha Muli',
    liveness_complete: 'Kumpletuhin ang Pagbili',
    liveness_processing: 'Pinoproseso...',

    // Processing
    processing_title: 'Pinoproseso ang Verification',
    processing_sub: 'Isinasagawa ang server-side identity verification...',
    processing_uploading: 'Ina-upload ang mga dokumento sa server',
    processing_ocr: 'Server OCR + face matching',
    processing_scoring: 'Kinakalkula ang confidence score',
    processing_record: 'Nag-ge-generate ng secure na verification record...',

    // Result
    result_title: 'Kumpleto na ang Authenticity Assessment',
    result_verified: 'Na-verify',
    result_download: 'I-download ang Digital ID',
    result_new: 'Bagong Verification',
    result_failed_title: 'Nabigo ang Verification',
    result_failed_sub: 'Hindi namin ma-verify ang iyong pagkakakilanlan. Subukan muli.',
    result_try_again: 'Subukan Muli',
    result_reasons: 'Mga Dahilan:',
    result_signed_note: 'Ang iyong Digital ID ay may RSA-4096 na digital signature. Maaaring i-verify ng mga bangko at employer ang pagiging tunay nito gamit ang Agos verification portal o sa pamamagitan ng pag-scan ng QR code.',

    // Footer
    footer_product: 'Produkto',
    footer_company: 'Kumpanya',
    footer_legal: 'Legal',
    footer_verify_record: 'I-verify ang Record',
    footer_pricing: 'Presyo',
    footer_dashboard: 'Dashboard',
    footer_faq: 'FAQ',
    footer_about: 'Tungkol sa Amin',
    footer_overview: 'Pangkalahatang-tanaw ng Produkto',
    footer_security: 'Security Whitepaper',
    footer_contact: 'Makipag-ugnay',
    footer_privacy: 'Patakaran sa Privacy',
    footer_terms: 'Mga Tuntunin ng Serbisyo',
    footer_brand_desc: 'Ang premium na identity verification protocol para sa Pilipinas. Engineering trust sa pamamagitan ng cryptographic excellence.',
    footer_npc_badge: 'NPC COMPLIANT • DPA READY',
    footer_bsp_badge: 'BSP COMPLIANCE READY',
    footer_npc_notice: 'Isinasagawa ang NPC registration. Beta testing gamit ang synthetic data. Ang production deployment (Q2 2026) ay gagamit ng LightNode Manila na may buong NPC compliance at DPA-ready architecture.',
    footer_bsp_notice: 'Dinisenyo para sa BSP eKYC Compliance',
  },
} as const;

export type TranslationKey = keyof typeof translations.en;

export function t(lang: Lang, key: TranslationKey): string {
  return (translations[lang] as Record<string, string>)[key] ?? (translations.en as Record<string, string>)[key] ?? key;
}
